import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import {
  ArrowRight,
  Bell,
  Briefcase,
  CornerDownLeft,
  LineChart,
  Search,
  SlidersHorizontal,
  Star,
} from 'lucide-react';
import { cx } from '@/lib/format';
import { useSearch } from '@/lib/queries';
import { Badge, Kbd } from '@/components/primitives';
import styles from './appShell.module.scss';

const QUICK_LINKS = [
  { label: 'Markets overview', to: '/markets', icon: LineChart },
  { label: 'Open the screener', to: '/screener', icon: SlidersHorizontal },
  { label: 'Your watchlist', to: '/watchlist', icon: Star },
  { label: 'Portfolio', to: '/portfolio', icon: Briefcase },
  { label: 'Price alerts', to: '/alerts', icon: Bell },
];

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const navigate = useNavigate();

  const { data: results = [] } = useSearch(query, 8);
  const total = results.length + (query ? 0 : QUICK_LINKS.length);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setActive(0);
    }
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  function go(index: number) {
    if (query && results[index]) {
      navigate(`/symbol/${encodeURIComponent(results[index].symbol)}`);
      onOpenChange(false);
      return;
    }
    if (!query && QUICK_LINKS[index]) {
      navigate(QUICK_LINKS[index].to);
      onOpenChange(false);
    }
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((current) => (total ? (current + 1) % total : 0));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => (total ? (current - 1 + total) % total : 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      go(active);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.paletteOverlay} />
        <Dialog.Content className={styles.palette} onKeyDown={onKeyDown}>
          <Dialog.Title className="sr-only">Search StockWatcher Pro</Dialog.Title>
          <div className={styles.paletteInputRow}>
            <Search size={16} className={styles.paletteSearchIcon} />
            <input
              className={styles.paletteInput}
              placeholder="Search a ticker or company…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              autoFocus
            />
            <Kbd>esc</Kbd>
          </div>

          <div className={styles.paletteBody}>
            {query && results.length === 0 ? (
              <div className={styles.paletteEmpty}>
                No securities match “{query}”. The live universe covers every US stock and ETF.
              </div>
            ) : null}

            {query
              ? results.map((security, index) => (
                  <button
                    key={security.symbol}
                    type="button"
                    className={cx(styles.paletteRow, index === active && styles.paletteRowActive)}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => go(index)}
                  >
                    <span className={styles.paletteSymbol}>{security.symbol}</span>
                    <span className={styles.paletteName}>{security.name}</span>
                    <Badge tone={security.type === 'etf' ? 'info' : 'neutral'}>
                      {security.type === 'etf' ? 'ETF' : security.exchange}
                    </Badge>
                    <span className={styles.palettePrice}>${security.price.toFixed(2)}</span>
                    <ArrowRight size={13} className={styles.paletteArrow} />
                  </button>
                ))
              : QUICK_LINKS.map((link, index) => {
                  const Icon = link.icon;
                  return (
                    <button
                      key={link.to}
                      type="button"
                      className={cx(styles.paletteRow, index === active && styles.paletteRowActive)}
                      onMouseEnter={() => setActive(index)}
                      onClick={() => go(index)}
                    >
                      <Icon size={15} className={styles.paletteQuickIcon} />
                      <span className={styles.paletteQuickLabel}>{link.label}</span>
                      <ArrowRight size={13} className={styles.paletteArrow} />
                    </button>
                  );
                })}
          </div>

          <div className={styles.paletteFoot}>
            <span>
              <Kbd>↑</Kbd> <Kbd>↓</Kbd> to navigate
            </span>
            <span>
              <CornerDownLeft size={11} /> to open
            </span>
            <span className={styles.paletteFootRight}>13,214 securities in the live universe</span>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
